import React from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import thunk from 'redux-thunk'

import { createStore, applyMiddleware, compose, combineReducers } from 'redux'
import { middleware as flashMiddleware } from 'redux-flash'
import { Provider } from 'react-redux'

import axios from 'shared/lib/axios'
import TypeContext from 'shared/TypeContext'
import { redirect } from 'shared/lib/helpers'
import {
  restoreInvitationPath,
  stashInvitationPath,
} from 'shared/lib/pendingInvitation'
import { RolesAppMapping } from 'shared/constants'
import { getParameterByName } from 'shared/utils'
import rootCustomerReducer from 'customer/rootReducer'
import rootPartnerReducer from 'partner/rootReducer'
import rootAdminReducer from 'admin/rootReducer'
import commonReducer from 'reducers/commonReducer'

import config, { exploreLocations } from './configuration'
import { setRetailerSession } from './actions/retailerActions'
import './i18n'

// Import global app styles.
import 'normalize.css'
import 'shared/assets/css/index.css'
import { BrowserRouter } from 'react-router-dom'

if (config.sentryEnabled) {
  Sentry.init({
    dsn: config.sentryDsn,
    environment: process.env.REACT_APP_STAGE,
  })
}

let App
let rootReducer
export let store
let dashboardType
let userData
let root

const startUp = () => {
  const flashOptions = {
    timeout: 5000,
    push: true,
  }
  const composeEnhancers =
    (process.env.NODE_ENV !== 'production' &&
      window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
    compose
  store = createStore(
    combineReducers({ ...commonReducer, ...rootReducer }),
    composeEnhancers(applyMiddleware(thunk, flashMiddleware(flashOptions)))
  )

  // Create root if it doesn't exist yet
  if (!root) {
    root = createRoot(document.getElementById('root'))
  }

  root.render(
    <Provider store={store}>
      <BrowserRouter>
        <TypeContext.Provider value={dashboardType}>
          <App user={userData} />
        </TypeContext.Provider>
      </BrowserRouter>
    </Provider>
  )
}

const initDynamicApp = (app, reducer, context) => {
  App = app
  rootReducer = reducer
  dashboardType = context

  return startUp()
}

// Temporary hack until we make API request to determine
// which application to load.
axios
  .get('/api/auth/user')
  .then((user) => {
    userData = user

    if (userData?.data?.referral_source) {
      const refSource = userData.data.referral_source
      localStorage.setItem('referralSource', refSource)
    }

    if (
      userData?.data?.discountOffers &&
      userData?.data?.discountOffers.length
    ) {
      const discountData = userData.data.discountOffers[0]
      localStorage.setItem('discountOffer', JSON.stringify(discountData))
    } else {
      localStorage.removeItem('discountOffer')
    }

    // Clear stale grants before any role branch: a previous mb_internal
    // session's array must never survive into another user's session —
    // the Data Panel filter trusts any array it finds.
    localStorage.removeItem('internalPermissions')

    // Before the router mounts, so an invitation clicked while signed out
    // resumes where it left off. Only the partner app routes it.
    restoreInvitationPath({
      canRenderInvitations:
        user.data.role === RolesAppMapping.PARTNER.role ||
        user.data.role === RolesAppMapping.PARTNER_LIMITED.role ||
        user.data.role === RolesAppMapping.PARTNER_ADMIN.role,
    })

    if (
      user.data.role === RolesAppMapping.PARTNER.role ||
      user.data.role === RolesAppMapping.PARTNER_LIMITED.role
    ) {
      import('./partner/App').then(({ default: app }) =>
        initDynamicApp(app, rootPartnerReducer, user.data.role)
      )
    } else if (user.data.role === RolesAppMapping.PARTNER_ADMIN.role) {
      import('./partner/App').then(({ default: app }) => {
        const retailerUuid = getParameterByName('onboarding_uuid')

        if (retailerUuid) {
          setRetailerSession(retailerUuid) // For onboarding check
        } else if (userData.data.retailers.length === 1) {
          setRetailerSession(userData.data.retailers[0].uuid)
        }

        return initDynamicApp(
          app,
          rootPartnerReducer,
          RolesAppMapping.PARTNER_ADMIN.role
        )
      })
    } else if (
      user.data.role === RolesAppMapping.MB_ADMIN.role ||
      user.data.role === RolesAppMapping.MB_INTERNAL.role ||
      user.data.role === RolesAppMapping.MB_USER.role ||
      user.data.role === RolesAppMapping.AUDITOR.role ||
      user.data.role === RolesAppMapping.PRICE_UPLOADER.role ||
      user.data.role === RolesAppMapping.FINANCE.role
    ) {
      // mb_internal users carry per-resource Data Panel grants; stash them so
      // the AdminRest resource list can filter what it renders. (Other roles
      // are covered by the unconditional clear above the dispatch.)
      if (user.data.role === RolesAppMapping.MB_INTERNAL.role) {
        localStorage.setItem(
          'internalPermissions',
          JSON.stringify(user.data.permissions || [])
        )
      }
      import('./admin/App').then(({ default: app }) => {
        initDynamicApp(app, rootAdminReducer, user.data.role)
      })
    } else if (user.data.role === RolesAppMapping.CUSTOMER.role) {
      import('./customer/App').then(({ default: app }) =>
        initDynamicApp(app, rootCustomerReducer, RolesAppMapping.CUSTOMER.role)
      )
    } else {
      stashInvitationPath()
      redirect(config.explore, exploreLocations.LOGIN)
    }
  })
  .catch(() => {
    stashInvitationPath()
    redirect(config.explore, exploreLocations.LOGIN)
  })
